import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import {
  X,
  FileText,
  AlertTriangle,
  AlertCircle,
  Loader2,
  Trash2,
  User,
  Settings,
  Bell,
  LogOut,
  Route,
  Copy,
} from 'lucide-react';
import { toast } from 'sonner';
import WellsList from '../components/WellsList';
import WellsListWithLocation from '../components/WellsListWithLocation';
import WellViewListings from '../components/WellViewListings';
import ComprehensiveWellForm from '../components/ComprehensiveWellForm';
import WellDetail from '../components/WellDetail';
import DataEntry from '../components/DataEntry';
import { DraftEntriesDashboard } from '../components/dataEntry/DraftEntriesDashboard';
import ProductionGraphs from '../components/ProductionGraphs';
import AnalyticsMobile from '../components/AnalyticsMobile';
import RoutesMobile from '../components/RoutesMobile';
import XMLImport from '../components/XMLImport';
import Notifications from '../components/Notifications';
import UserManagement from '../components/UserManagement';
import Dashboard from '../components/Dashboard';
import ExitConfirmDialog from '../components/ExitConfirmDialog';

import WellComparison from '../components/WellComparison';
import OfflineIndicator from '../components/OfflineIndicator';
import SmartAlerts from '../components/SmartAlerts';
import AdvancedAnalytics from '../components/AdvancedAnalytics';
import SatelliteCommand from '../components/map/SatelliteCommand';
import IndustryMap from '../components/manager/IndustryMap';
import TankMonitor from '../components/map/TankMonitor';
import SurveillanceHub from '../components/map/SurveillanceHub';
import TimeClock from '../components/TimeClock';
import ConflictResolver from '../components/ConflictResolver';
import MessagingCenter from '../components/MessagingCenter';
import ShiftHandoff from '../components/ShiftHandoff';
import TaskManager from '../components/TaskManager';
import XAssist from '../components/XAssist';
import SecuritySetup from '../components/SecuritySetup';
import LockScreen from '../components/LockScreen';
import PhotoAnnotator from '../components/PhotoAnnotator';
import SettingsTab from '../components/SettingsTab';
import LoginScreen from '../components/auth/LoginScreen';
import UserSettings from '../components/UserSettings';
import NotificationSettings from '../components/NotificationSettings';
import CompanyManagement from '../components/CompanyManagement';
import BillingDashboard from '../components/BillingDashboard';
import ServiceTickets from '../components/ServiceTickets';
import SuperAdminDashboard from '../components/SuperAdminDashboard';
import AlertCenter from '../components/AlertCenter';
import WellCheckDashboard from '../components/WellCheckDashboard';
import EquipmentManagement from '../components/EquipmentManagement';
import { MediaGallery } from '../components/MediaGallery';
import { NavigatorLaunchDialog } from '../components/NavigatorLaunchDialog';
import UserInviteModal from '../components/UserInviteModal';
import { useWells } from '../hooks/useWells';
import { useNotifications } from '../hooks/useNotifications';
import { alertEngine } from '../utils/alertEngine';
import { indexedDBService } from '../utils/offline/indexedDB';
import { syncQueueManager } from '../utils/offline/syncQueue';
import { autoLockService } from '../utils/security/autoLock';
// 🏢 Multi-tenant context
import { getCompanyId, getCompanyDisplayName, isSuperAdmin, logCompanyContext } from '../utils/tenantContext';
import { getServiceTickets } from '../utils/services/serviceTicketService';
import { taskService } from '../utils/tasks/taskService';
import { getUsersByCompany } from '../utils/auth/userService';
import { fetchWellEntries } from '../utils/firebase/api';
// Auth & Services
import { authService } from '../utils/auth/authService';

// Field optimization services
import { syncStatus } from '../utils/sync/syncStatus';
// Feature flags
import { isFeatureEnabled } from '../utils/featureFlags';
// REMOVED: Old timer-based SyncService - Now using event-driven enterpriseSync
import { AdminDebugPanel } from '../components/AdminDebugPanel';
// Firebase imports for fetching company data
import { db } from '../utils/firebase/client';
import { collection, query, where, getDocs, onSnapshot } from 'firebase/firestore';
import { deleteWellDocument } from '../utils/firebase/database';
import { deleteStorageFolder } from '../utils/firebase/storage';
import { ParsedCommand } from '../utils/voice/commandParser';
import { wakeWordService } from '../utils/voice/wakeWordService';
import { voiceCommandExecutor, VoiceCommandContext } from '../utils/voice/voiceCommandExecutor';
import { voiceNavigationEngine } from '../utils/voice/voiceNavigationEngine';
import { AppLayout } from '../components/AppLayout';
import VendorOnboardingForm from '../components/VendorOnboardingForm';
import PublicVendorRegistration from '../components/PublicVendorRegistration';
import TermsOfService from '../components/TermsOfService';
import PrivacyPolicy from '../components/PrivacyPolicy';
import { resolveWellCoordinates } from '../utils/location/coordinates';
import { startNavigation, startRouteNavigation, getUserNavigationPreferences } from '../utils/navigation/navigationCoordinator';
import { offlineNavigationService } from '../utils/navigation/offlineNavigationService';
import { arrivalNotificationService } from '../utils/navigation/arrivalNotificationService';
import { routeHazardService } from '../utils/navigation/routeHazardService';
import { wellDepartureService, WellDepartureEvent } from '../utils/navigation/wellDepartureService';
import WellDepartureReminder from '../components/notifications/WellDepartureReminder';

import { useTheme } from '../hooks/useTheme';
import { useNavigationHistory } from '../hooks/useNavigationHistory';
import { shouldRemindClockIn, shouldRemindClockOut, timeClockService } from '../utils/services/timeClockService';

interface PumpersPortalProps {
  viewAsField?: boolean;
  onReturnToCommandCenter?: () => void;
}

export default function PumpersPortal({ viewAsField = false, onReturnToCommandCenter }: PumpersPortalProps) {
  // 🔓 PUBLIC ROUTE CHECK: Public routes bypass authentication
  const pathname = window.location.pathname;

  // Terms of Service page
  if (pathname === '/terms' || pathname === '/terms-of-service') {
    return <TermsOfService />;
  }

  // Privacy Policy page
  if (pathname === '/privacy-policy' || pathname === '/privacy') {
    return <PrivacyPolicy />;
  }

  // Public vendor registration (no token required)
  if (pathname === '/vendor/register' || pathname === '/vendors/apply') {
    return <PublicVendorRegistration />;
  }

  // Vendor onboarding with invitation token
  if (pathname.startsWith('/vendor/onboard/')) {
    const token = pathname.replace('/vendor/onboard/', '');
    return <VendorOnboardingForm token={token} />;
  }

  // Authentication state
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<any>(null);

  // Theme Management
  const { isDarkMode, toggleTheme } = useTheme(user?.uid);

  // ALL OTHER STATE HOOKS MUST COME BEFORE ANY CONDITIONAL RETURNS
  // 🔒 SECURITY: Pass full user object for tenant validation
  const { wells, loading: wellsLoading, backendConnected, refreshWells, updateWell } = useWells(user?.id, user);
  const {
    notifications,
    unreadCount,
    refreshNotifications,
    createNotification,
    updateNotification,
  } = useNotifications();

  // Expose updateWell globally for ServiceTickets component
  useEffect(() => {
    (window as any).updateWell = updateWell;
    (window as any).createNotification = createNotification;
    return () => {
      delete (window as any).updateWell;
      delete (window as any).createNotification;
    };
  }, [updateWell, createNotification]);

  const [activeTab, setActiveTab] = useState('dashboard');
  const [wellListFilter, setWellListFilter] = useState('all');
  const [wellListSort, setWellListSort] = useState<{ key: string; direction: 'asc' | 'desc' } | null>(null);
  const [selectedWell, setSelectedWell] = useState<any>(null);
  const [serviceTicketWellId, setServiceTicketWellId] = useState<string | null>(null);
  const [taskWellId, setTaskWellId] = useState<string | null>(null);
  const [showDataEntry, setShowDataEntry] = useState(false);
  const [showDraftsDashboard, setShowDraftsDashboard] = useState(false);
  const [showWellEditor, setShowWellEditor] = useState(false);
  const [wellToEdit, setWellToEdit] = useState<any>(null);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [wellToDelete, setWellToDelete] = useState<any>(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showComparison, setShowComparison] = useState(false);
  const [showAlerts, setShowAlerts] = useState(false);
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [showMap, setShowMap] = useState(false);
  const [showTimeClock, setShowTimeClock] = useState(false);
  const [isClockedIn, setIsClockedIn] = useState(false);
  const [smartAlerts, setSmartAlerts] = useState<any[]>([]);
  const [showMessaging, setShowMessaging] = useState(false);
  const [showTaskManager, setShowTaskManager] = useState(false);
  const [showShiftHandoff, setShowShiftHandoff] = useState(false);
  const [showConflictResolver, setShowConflictResolver] = useState(false);
  const [showXAssist, setShowXAssist] = useState(false);
  const [showSecuritySetup, setShowSecuritySetup] = useState(false);
  const [isLocked, setIsLocked] = useState(false);
  const [showPhotoAnnotator, setShowPhotoAnnotator] = useState(false);
  const [photoToAnnotate, setPhotoToAnnotate] = useState<string | null>(null);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showUserSettings, setShowUserSettings] = useState(false);
  const [showNotificationSettings, setShowNotificationSettings] = useState(false);
  const [showBillingDashboard, setShowBillingDashboard] = useState(false);
  const [showCompanyManagement, setShowCompanyManagement] = useState(false);
  const [showDebugPanel, setShowDebugPanel] = useState(false);
  const [showWellChecks, setShowWellChecks] = useState(false);
  const [showEquipment, setShowEquipment] = useState(false);
  const [showGallery, setShowGallery] = useState(false);
  const [isVoiceListening, setIsVoiceListening] = useState(false);
  const [showAlertCenter, setShowAlertCenter] = useState(false);
  const [unreadAlertCount, setUnreadAlertCount] = useState(0);
  const [showNavigation, setShowNavigation] = useState(false);
  const [showLaunchDialog, setShowLaunchDialog] = useState(false);
  const [navigationRoute, setNavigationRoute] = useState<any | null>(null);
  const [showIndustryMap, setShowIndustryMap] = useState(false);

  // Well Departure Reminder State
  const [departureReminder, setDepartureReminder] = useState<{
    isVisible: boolean;
    wellId: string;
    wellName: string;
    dwellDurationMs: number;
  } | null>(null);

  // Satellite Command Extended Features
  const [showTankMonitor, setShowTankMonitor] = useState(false);
  const [showSurveillance, setShowSurveillance] = useState(false);
  const [monitorWell, setMonitorWell] = useState<any>(null);

  // Service tickets and pumpers data
  const [serviceTickets, setServiceTickets] = useState<any[]>([]);
  const [tasks, setTasks] = useState<any[]>([]);
  const [pumpers, setPumpers] = useState<any[]>([]);
  const [loadingTickets, setLoadingTickets] = useState(false);
  const [loadingPumpers, setLoadingPumpers] = useState(false);

  // 📱 Navigation History - Enable proper back button behavior for PWA
  const { showExitConfirmDialog, handleConfirmExit, handleCancelExit } = useNavigationHistory({
    activeTab,
    setActiveTab,
    selectedWellId: selectedWell?.id || null,
    setSelectedWellId: (id) => {
      if (id) {
        const well = wells.find(w => w.id === id);
        setSelectedWell(well || null);
      } else {
        setSelectedWell(null);
      }
    },
    options: {
      defaultTab: 'dashboard',
      preventExit: true,
      showExitDialog: true
    }
  });

  // Load service tickets and pumpers data
  useEffect(() => {
    if (!user || !isAuthenticated) {
      setServiceTickets([]);
      setTasks([]);
      setPumpers([]);
      return;
    }

    // Subscribe to tasks
    const unsubscribeTasks = taskService.subscribeToTasks((updatedTasks) => {
      setTasks(updatedTasks);
    });

    // 🚨 Initialize Alert System
    const initAlerts = async () => {
      try {
        const { initializeAlertSystem } = await import('../utils/alerts');
        const userCompanyId = user?.companyId || getCompanyId();
        if (userCompanyId && userCompanyId !== 'superadmin') {
          await initializeAlertSystem(userCompanyId);
        }
      } catch (error) {
      }
    };
    initAlerts();

    const loadData = async () => {
      try {
        // Load service tickets
        setLoadingTickets(true);
        const tickets = await getServiceTickets({
          includeCompleted: false, // Only active tickets
          limit: 100
        });
        setServiceTickets(tickets);
      } catch (error: any) {
        console.error('Failed to load service tickets:', error);
        // Graceful handling for missing Firestore index
        if (error?.message?.includes('requires an index')) {
          toast.error('Tickets index required', {
            description: 'Database index setup needed - app will continue without tickets',
            duration: 5000,
          });
        }
        setServiceTickets([]);
      } finally {
        setLoadingTickets(false);
      }

      try {
        // Load pumpers (field workers) - ONLY MANAGERS CAN LIST ALL USERS
        // Pumpers don't have permission to list users, skip for them
        if (user?.role === 'manager' || user?.role === 'admin' || user?.role === 'superadmin' || user?.isSuperAdmin) {
          setLoadingPumpers(true);
          const allUsers = await getUsersByCompany({
            isActive: true,
            limit: 100
          });
          const pumperUsers = allUsers.filter(u =>
            u.role === 'pumper' || u.role === 'mechanic' || u.role === 'contractor'
          );
          setPumpers(pumperUsers);
        } else {
          // Non-managers: just skip loading users
          setPumpers([]);
        }
      } catch (error: any) {
        console.error('Failed to load pumpers:', error);
        // Graceful handling for permission denied or missing index
        if (error?.message?.includes('permission') || error?.message?.includes('requires an index')) {
          // Silent failure - user doesn't have access to list users
        }
        setPumpers([]);
      } finally {
        setLoadingPumpers(false);
      }
    };

    loadData();

    return () => {
      unsubscribeTasks();
    };
  }, [user, isAuthenticated]);

  // Check if SuperAdmin mode is enabled based on user
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [companyName, setCompanyName] = useState('Loading...');
  const [companyLogo, setCompanyLogo] = useState<string | null>(null);

  // 🏢 Multi-tenant context
  const [companyId, setCompanyId] = useState('default');
  const [companyDisplayName, setCompanyDisplayName] = useState('OilWellX');

  const toggleVoiceListening = () => {
    setIsVoiceListening((prev: boolean) => {
      const newState = !prev;
      if (newState) {
        toast.success('Voice Assistant Active', {
          description: 'Say "Hey X" or "Hi X" to start',
          duration: 3000
        });
      } else {
        toast.info('Voice Assistant Paused');
      }
      return newState;
    });
  };

  const openNavigation = async (route: any) => {
    // Handle both "wells" (InAppNavigation format) and "waypoints" (OptimizedRoute format)
    const stops = route.wells || route.waypoints;

    if (!stops || stops.length === 0) {
      toast.error('No destinations in this route. Please select wells and generate a route first.');
      return;
    }

    // Normalize the route to the format InAppNavigation expects
    const normalizedRoute = {
      ...route,
      wells: stops.map((stop: any, index: number) => {
        // Handle both coordinate formats
        const coords = stop.coordinates || { lat: stop.latitude, lng: stop.longitude };

        return {
          wellId: stop.wellId || stop.id,
          wellName: stop.wellName || stop.name,
          order: stop.order || index + 1,
          coordinates: coords,
          estimatedTime: stop.estimatedTime || (stop.estimatedServiceTime ? stop.estimatedServiceTime / 60 : 0.5)
        };
      })
    };

    // Validate coordinates
    const invalidStop = normalizedRoute.wells.find(
      (stop: any) => !stop.coordinates || typeof stop.coordinates.lat !== 'number' || typeof stop.coordinates.lng !== 'number'
    );

    if (invalidStop) {
      toast.error(`Missing GPS coordinates for ${invalidStop.wellName || 'a stop'}`);
      return;
    }

    // Use the unified navigation coordinator
    const preferences = getUserNavigationPreferences(user);

    await startRouteNavigation(normalizedRoute, preferences, {
      onStartInternalNavigation: (navRoute) => {
        setNavigationRoute(navRoute);
        setShowLaunchDialog(true);
      }
    });
  };

  const closeNavigation = () => {
    setShowNavigation(false);
    setNavigationRoute(null);
  };

  const handleNavigateToWell = async (well: any) => {
    const coordinates = resolveWellCoordinates(well);
    if (!coordinates) {
      toast.error('This well is missing GPS coordinates');
      return;
    }

    // Use the unified navigation coordinator
    const preferences = getUserNavigationPreferences(user);

    await startNavigation(
      {
        latitude: coordinates.lat,
        longitude: coordinates.lng,
        name: well.name,
        wellId: well.id,
        address: well.location,
      },
      preferences,
      {
        onStartInternalNavigation: (route) => {
          setNavigationRoute(route);
          setShowLaunchDialog(true);
        }
      }
    );
  };

  const handleWakeWordDetected = useCallback((keyword: string) => {
    if (!isVoiceListening) {
      return;
    }

    setShowXAssist(prev => {
      if (prev) {
        return prev;
      }
      toast.info('Wake word detected', {
        description: keyword === 'hi-x' ? 'Hi X is ready to help' : 'Hey X activated',
        duration: 2500,
      });
      return true;
    });
  }, [isVoiceListening]);

  // Initialize company context
  useEffect(() => {
    const currentCompanyId = getCompanyId();
    const currentCompanyName = getCompanyDisplayName();

    setCompanyId(currentCompanyId);
    setCompanyDisplayName(currentCompanyName);

    // Log tenant context for debugging (only in development)
    if (window.location.hostname.includes('localhost')) {
      logCompanyContext();
    }

  }, []);

  // Check authentication on mount
  useEffect(() => {
    const currentUser = authService.getCurrentUser();
    if (currentUser) {
      setUser(currentUser);
      setIsAuthenticated(true);
      setIsSuperAdmin(currentUser.isSuperAdmin || currentUser.role === 'superadmin');

      // Company name is fetched via real-time listener when user.companyId changes

      // Log superadmin status for debugging
    }
  }, []);

  useEffect(() => {
    const handleShortcut = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.shiftKey && (event.key === 'v' || event.key === 'V')) {
        event.preventDefault();
        setShowXAssist(prev => !prev);
      }
    };

    window.addEventListener('keydown', handleShortcut);
    return () => window.removeEventListener('keydown', handleShortcut);
  }, []);

  // Real-time listener for unread alert count
  useEffect(() => {
    if (!user || !user.companyId) return;

    const alertsRef = collection(db, `companies/${user.companyId}/alerts`);

    // 🔒 SECURITY: Filter by recipient to match Firestore rules
    // Rules require: resource.data.recipientIds.hasAny([request.auth.uid])
    let unreadQuery;

    if (user.role === 'superadmin' || user.isSuperAdmin === true) {
      // SuperAdmins can see all alerts
      unreadQuery = query(
        alertsRef,
        where('status', 'in', ['new', 'acknowledged', 'in_progress'])
      );
    } else {
      // Regular users can only see alerts sent to them
      unreadQuery = query(
        alertsRef,
        where('status', 'in', ['new', 'acknowledged', 'in_progress']),
        where('recipientIds', 'array-contains', user.id || user.uid)
      );
    }

    const unsubscribe = onSnapshot(unreadQuery, (snapshot) => {
      setUnreadAlertCount(snapshot.size);
    }, (_error) => {
      // Silently fail - user may not have permission to read alerts
      // This is expected for some user roles
      setUnreadAlertCount(0);
    });

    return () => unsubscribe();
  }, [user, isAuthenticated]);

  // ⏰ Time Clock Reminders
  useEffect(() => {
    if (!user || !isAuthenticated) return;

    const checkReminders = async () => {
      try {
        const userId = user.id || user.uid;

        // Check clock in reminder
        if (await shouldRemindClockIn(userId)) {
          toast.info('Shift Reminder', {
            description: 'Your shift started recently. Don\'t forget to clock in!',
            action: {
              label: 'Clock In',
              onClick: () => setShowTimeClock(true)
            },
            duration: 10000,
          });

          createNotification({
            title: 'Clock In Reminder',
            message: 'Your shift has started. Please clock in.',
            type: 'alert',
            read: false,
            timestamp: new Date().toISOString()
          });
        }

        // Check clock out reminder
        if (await shouldRemindClockOut(userId)) {
          toast.warning('Shift End Reminder', {
            description: 'Your shift has ended. Please clock out if you are done.',
            action: {
              label: 'Clock Out',
              onClick: () => setShowTimeClock(true)
            },
            duration: 10000,
          });

          createNotification({
            title: 'Clock Out Reminder',
            message: 'Your shift has ended. Please clock out.',
            type: 'alert',
            read: false,
            timestamp: new Date().toISOString()
          });
        }
      } catch (error) {
        console.error('Error checking time clock reminders:', error);
      }
    };

    // Check immediately and then every 5 minutes
    checkReminders();
    const interval = setInterval(checkReminders, 5 * 60 * 1000);

    return () => clearInterval(interval);
  }, [user, isAuthenticated, createNotification]);

  // Initialize isClockedIn state on app load
  useEffect(() => {
    if (!user?.id || !isAuthenticated) {
      setIsClockedIn(false);
      return;
    }

    const checkClockStatus = async () => {
      try {
        const active = await timeClockService.getActiveEntry(user.id);
        setIsClockedIn(!!active);
      } catch (error) {
        console.error('Failed to check clock status:', error);
        setIsClockedIn(false);
      }
    };

    checkClockStatus();
  }, [user?.id, isAuthenticated]);

  // Real-time listener for Company Name & Logo
  useEffect(() => {
    if (!user || !user.companyId || user.companyId === 'default' || user.companyId === 'superadmin') {
      setCompanyName('OilWellX');
      setCompanyLogo(null);
      return;
    }

    const companiesRef = collection(db, 'companies');
    const q = query(companiesRef, where('companyId', '==', user.companyId));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      if (!snapshot.empty) {
        const companyData = snapshot.docs[0].data();
        // Prioritize displayName, then name, then companyName
        const name = companyData.displayName || companyData.name || companyData.companyName || 'OilWellX';
        setCompanyName(name);

        // Set company logo if available
        const logo = companyData.branding?.logo || companyData.branding?.logoUrl || null;
        setCompanyLogo(logo);

        // Update user object with company info if needed
        // Note: We don't update 'user' state here to avoid infinite loops, 
        // but the UI uses 'companyName' and 'companyLogo' state variables.
      } else {
        setCompanyName('OilWellX');
        setCompanyLogo(null);
      }
    }, (error) => {
      console.error('Error listening to company data:', error);
    });

    return () => unsubscribe();
  }, [user?.companyId]);

  // Handle login success
  const handleLoginSuccess = async (loggedInUser: any) => {
    setUser(loggedInUser);
    setIsAuthenticated(true);
    setIsSuperAdmin(loggedInUser.isSuperAdmin || loggedInUser.role === 'superadmin');
    // Company data fetch is now handled by the useEffect above
    toast.success(`Welcome back, ${loggedInUser.name}!`);

    // 🚀 Initialize navigation services
    try {
      // Initialize offline navigation service
      await offlineNavigationService.initialize();

      // Initialize arrival notification service
      if (loggedInUser.companyId) {
        arrivalNotificationService.initialize(
          loggedInUser.companyId,
          loggedInUser.id || loggedInUser.uid,
          loggedInUser.name || loggedInUser.email
        );

        // Initialize hazard service
        routeHazardService.initialize(loggedInUser.companyId);
      }
    } catch (error) {
      console.error('Failed to initialize navigation services:', error);
      // Non-critical - app will still work without these
    }
  };

  // Handle well departure reminder callback
  const handleWellDeparture = useCallback((event: WellDepartureEvent) => {
    console.log('[PumpersPortal] Well departure detected:', event.wellName);
    setDepartureReminder({
      isVisible: true,
      wellId: event.wellId,
      wellName: event.wellName,
      dwellDurationMs: event.dwellDurationMs,
    });
  }, []);

  // Initialize well departure service
  useEffect(() => {
    if (!user || !isAuthenticated || wells.length === 0) {
      wellDepartureService.stop();
      return;
    }

    // Get user notification preferences
    const notificationPrefs = user.preferences?.notificationSettings;
    const missedDataEntryEnabled = notificationPrefs?.missedDataEntry?.enabled ?? true;
    const allEnabled = notificationPrefs?.all_notifications_enabled ?? true;

    // Prepare wells with coordinates
    const wellsWithCoords = wells
      .filter((w: any) => w.latitude && w.longitude)
      .map((w: any) => ({
        id: w.id,
        name: w.name || w.lease_name || 'Unknown Well',
        latitude: w.latitude,
        longitude: w.longitude,
      }));

    // Start service
    wellDepartureService.start({
      wells: wellsWithCoords,
      userId: user.id || user.uid,
      onDeparture: handleWellDeparture,
      notificationPrefs: {
        enabled: allEnabled,
        missedDataEntry: {
          enabled: missedDataEntryEnabled,
          sound: notificationPrefs?.missedDataEntry?.sound ?? true,
          vibrate: notificationPrefs?.missedDataEntry?.vibrate ?? true,
        },
      },
    });

    return () => {
      wellDepartureService.stop();
    };
  }, [user, isAuthenticated, wells, handleWellDeparture]);

  // Handle logout
  const handleLogout = async () => {

    try {
      // Clear all auth data FIRST before Firebase signout
      // This prevents race conditions where auth state listener re-authenticates
      localStorage.removeItem('oilwellx_auth_user');
      localStorage.removeItem('oilwellx_auth_session');
      sessionStorage.removeItem('oilwellx_auth_user');
      sessionStorage.removeItem('oilwellx_auth_session');

      // Clear state immediately
      setUser(null);
      setIsAuthenticated(false);
      setIsSuperAdmin(false);
      setIsLocked(false);
      setActiveTab('dashboard');

      // 🧹 Cleanup navigation services
      routeHazardService.dispose();

      // Now perform Firebase signout
      await authService.logout();

      toast.success('Logged out successfully');
    } catch (error) {
      console.error('Logout error:', error);
      // Force logout even on error
      setUser(null);
      setIsAuthenticated(false);
      toast.error('Logout completed with warnings');
    }
  };

  // Initialize IndexedDB and auto-lock on app start
  useEffect(() => {
    // Check if ACTUALLY initialized, not just the flag
    if ((window as any).__offlineSystemInitialized && indexedDBService.isInitialized()) {
      return;
    }

    const initOfflineSystem = async () => {
      try {
        // Only initialize if not already initialized
        if (!indexedDBService.isInitialized()) {
          await indexedDBService.initialize();
        }

        // REMOVED: Timer-based auto-sync - Now using event-driven enterpriseSync
        // syncQueueManager.startAutoSync(30000);

        // Mark as initialized ONLY after successful initialization
        (window as any).__offlineSystemInitialized = true;
      } catch (error) {
        console.error('Failed to initialize offline system:', error);
        // Reset the flag on error so it can retry
        (window as any).__offlineSystemInitialized = false;
      }
    };

    initOfflineSystem();
  }, []); // Run only once on mount

  // Separate effect for auto-lock listener
  useEffect(() => {
    if (!isAuthenticated || !user) return;

    const handleAutoLock = (locked: boolean) => {
      setIsLocked(locked);
      if (locked) {
        toast.info('App locked due to inactivity');
      }
    };

    autoLockService.addListener(handleAutoLock);

    return () => {
      autoLockService.removeListener(handleAutoLock);
    };
  }, [isAuthenticated, user]);

  // Separate effect for saving wells to IndexedDB when wells change
  useEffect(() => {
    const saveWellsToIndexedDB = async () => {
      if (wells.length > 0) {
        try {
          await indexedDBService.saveWells(wells);
        } catch (error) {
          console.error('Failed to save wells to IndexedDB:', error);
        }
      }
    };

    saveWellsToIndexedDB();
  }, [wells]);

  useEffect(() => {
    let isUnmounted = false;

    const initWakeWord = async () => {
      if (typeof window === 'undefined') {
        return;
      }
      try {
        await wakeWordService.initialize({
          accessKey: (import.meta as any).env?.VITE_PICOVOICE_ACCESS_KEY,
          language: navigator.language || 'en-US',
        });
        if (isUnmounted) {
          return;
        }
        wakeWordService.addListener(handleWakeWordDetected);
        // DON'T start automatically - wait for user to enable voice listening
        // await wakeWordService.start();
      } catch (error) {
      }
    };

    initWakeWord();

    return () => {
      isUnmounted = true;
      wakeWordService.removeListener(handleWakeWordDetected);
      wakeWordService.stop();
    };
  }, [handleWakeWordDetected]);

  useEffect(() => {
    if (!wakeWordService) {
      return;
    }
    if (showXAssist || isLocked || !isVoiceListening) {
      wakeWordService.stop();
    } else if (isVoiceListening) {
      void wakeWordService.start();
    }
  }, [showXAssist, isLocked, isVoiceListening]);

  // =========================================================================
  // TASK 1.3: Fetch production entries when a well is selected
  // =========================================================================
  useEffect(() => {
    if (!selectedWell?.id) return;

    const loadWellEntries = async () => {
      try {
        const companyId = getCompanyId();
        const entries = await fetchWellEntries(companyId, selectedWell.id, 10);

        // Update selectedWell with production entries
        setSelectedWell((prev: any) =>
          prev && prev.id === selectedWell.id
            ? { ...prev, productionEntries: entries }
            : prev
        );
      } catch (error) {
        console.error(`[Task 1.3] Failed to load entries for ${selectedWell.name}:`, error);
        // Don't show error toast - well still works, just without entries display
      }
    };

    loadWellEntries();
  }, [selectedWell?.id]); // Only re-fetch when well ID changes

  // Filter wells based on search term
  const filteredWells = wells.filter(well =>
    well.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    well.location?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    well.company?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Get overdue wells (not checked in last 7 days)
  const overdueWells = wells.filter(well => {
    if (!well.lastEntry) return true;
    const daysSinceLastEntry = (Date.now() - new Date(well.lastEntry).getTime()) / (1000 * 60 * 60 * 24);
    return daysSinceLastEntry > 7;
  });

  const findWellByIdentifier = (identifier?: string) => {
    if (!identifier) return undefined;
    const normalized = identifier.toString().toLowerCase();
    return wells.find(w =>
      w.id === identifier ||
      w.api_number === identifier ||
      w.name?.toLowerCase().includes(normalized)
    );
  };

  const handleAssistantCommand = async (cmd: Partial<ParsedCommand> & { type?: string; target?: string; wellId?: string }) => {
    const action = cmd.action || cmd.type;
    if (!action) {
      return;
    }

    const parameters = cmd.parameters || {};
    const target = parameters.page || cmd.target;
    const wellId = parameters.wellId || cmd.wellId;

    const openWellById = (identifier?: string) => {
      const well = findWellByIdentifier(identifier);
      if (well) {
        setSelectedWell(well);
        setActiveTab('wells');
        toast.success(`Viewing ${well.name}`);
        return true;
      }
      if (identifier) {
        toast.error(`Well not found: ${identifier}`);
      }
      return false;
    };

    // Build voice execution context for the executor
    const executionContext: VoiceExecutionContext = {
      userId: user?.id || '',
      companyId: getCompanyId(),
      userRole: user?.role || 'pumper',
      currentWellId: selectedWell?.id,
      currentScreen: activeTab,
      wells: wells,
      isOnline: navigator.onLine,
      timestamp: Date.now()
    };

    // --- Execute with Voice Command Executor for enterprise actions ---
    const enterpriseActions = [
      'clock_in', 'clock_out', 'CLOCK_IN', 'CLOCK_OUT',
      'start_break', 'end_break', 'START_BREAK', 'END_BREAK',
      'add_note', 'ADD_NOTE',
      'start_navigation', 'stop_navigation', 'START_NAVIGATION', 'STOP_NAVIGATION',
      'report_hazard', 'REPORT_HAZARD',
      'emergency_stop', 'EMERGENCY_STOP',
      'check_status', 'CHECK_STATUS',
      'show_production', 'SHOW_PRODUCTION',
      'mark_complete', 'MARK_COMPLETE',
      'call_supervisor', 'CALL_SUPERVISOR'
    ];

    if (enterpriseActions.includes(action)) {
      try {
        const result = await voiceCommandExecutor.executeCommand(
          { action, parameters: parameters as Record<string, any>, intent: cmd.intent || 'unknown', confidence: cmd.confidence || 0.5, originalText: cmd.originalText || '' },
          executionContext
        );

        if (result.success) {
          // Handle navigation results
          if (result.data?.navigationStarted && result.data?.route) {
            setNavigationRoute(result.data.route);
            setShowLaunchDialog(true);
          }
          // Handle UI state changes from executor
          if (result.data?.screenChange) {
            setActiveTab(result.data.screenChange);
          }
          if (result.data?.wellId) {
            openWellById(result.data.wellId);
          }
        }
        return;
      } catch (error) {
        console.error('Voice executor error:', error);
        toast.error('Command execution failed');
        return;
      }
    }

    // --- Standard Voice Command IDs (Uppercase) ---
    if (action === 'NAVIGATE_WELLS') {
      setActiveTab('wells');
      setSelectedWell(null);
      toast.success('Opening wells list');
      return;
    }
    if (action === 'NAVIGATE_DASHBOARD') {
      setActiveTab('dashboard');
      toast.success('Opening dashboard');
      return;
    }
    if (action === 'NAVIGATE_MAP') {
      setShowMap(true);
      toast.success('Opening map view');
      return;
    }
    if (action === 'NAVIGATE_ANALYTICS') {
      setShowAnalytics(true);
      toast.success('Opening analytics');
      return;
    }
    if (action === 'NAVIGATE_TICKETS') {
      setActiveTab('tickets');
      toast.success('Opening service tickets');
      return;
    }
    if (action === 'NAVIGATE_ROUTES') {
      setActiveTab('routes');
      toast.success('Opening route optimizer');
      return;
    }
    if (action === 'CREATE_TICKET') {
      setActiveTab('tickets');
      toast.success('Opening service tickets');
      return;
    }
    if (action === 'NAVIGATE_TIMECLOCK' || action === 'navigate_timeclock') {
      setActiveTab('timeclock');
      toast.success('Opening time clock');
      return;
    }
    // ----------------------------------------------

    if (action === 'navigate') {
      if (target === 'well' && wellId) {
        openWellById(wellId);
      } else if (target === 'wells') {
        setActiveTab('wells');
        setSelectedWell(null);
        toast.success('Opening wells list');
      } else if (target === 'dashboard') {
        setActiveTab('dashboard');
        toast.success('Opening dashboard');
      } else if (target === 'analytics') {
        setShowAnalytics(true);
        toast.success('Opening analytics');
      } else if (target === 'map') {
        setShowMap(true);
        toast.success('Opening map view');
      } else if (target === 'tickets') {
        setActiveTab('tickets');
        toast.success('Opening service tickets');
      } else if (target === 'routes') {
        setActiveTab('routes');
        toast.success('Opening route optimizer');
      } else if (target === 'timeclock') {
        setActiveTab('timeclock');
        toast.success('Opening time clock');
      } else if (typeof target === 'string') {
        setActiveTab(target);
        toast.success(`Opening ${target}`);
      }
      return;
    }

    if (action === 'go_to_well' || action === 'show_satellite_for_well') {
      openWellById(wellId || (parameters as any).well);
      return;
    }

    if (action === 'go_to_page') {
      const page = target || parameters.page;
      if (!page) return;
      if (page === 'analytics') {
        setShowAnalytics(true);
      } else if (page === 'map') {
        setShowMap(true);
      } else if (page === 'routes') {
        setActiveTab('routes');
      } else if (page === 'tickets') {
        setActiveTab('tickets');
      } else if (page === 'timeclock') {
        setActiveTab('timeclock');
      } else {
        setActiveTab(page);
      }
      toast.success(`Opening ${page}`);
      return;
    }

    if (action === 'show_directions' || action === 'SHOW_DIRECTIONS') {
      const well = findWellByIdentifier(wellId);
      if (well && well.latitude && well.longitude) {
        setSelectedWell(well);

        // Start voice navigation if coordinates are available
        try {
          const destination = { lat: well.latitude, lng: well.longitude };
          toast.info(`Getting directions to ${well.name}...`);

          // Get current position
          navigator.geolocation.getCurrentPosition(
            async (position) => {
              const origin = { lat: position.coords.latitude, lng: position.coords.longitude };

              // Start the voice navigation engine
              const started = await voiceNavigationEngine.startNavigation(
                origin,
                destination,
                well.name
              );

              if (started) {
                setShowLaunchDialog(true);
                toast.success(`Voice navigation started to ${well.name}`);
              } else {
                // Fallback to map view
                setShowMap(true);
                toast.info(`Showing ${well.name} on map`);
              }
            },
            (error) => {
              console.error('Geolocation error:', error);
              setShowMap(true);
              toast.info(`Showing ${well.name} on map`);
            },
            { enableHighAccuracy: true, timeout: 10000 }
          );
        } catch (error) {
          console.error('Navigation error:', error);
          setShowMap(true);
          toast.success(`Showing directions to ${well.name}`);
        }
      } else if (well) {
        setSelectedWell(well);
        setShowMap(true);
        toast.info(`${well.name} has no coordinates - showing on map`);
      } else {
        toast.error(`Well not found: ${wellId}`);
      }
      return;
    }

    if (action === 'show_wells_by_status' || action === 'filter') {
      setActiveTab('wells');
      setSelectedWell(null);
      toast.success('Filtering wells...');
      // TODO: Apply actual filters in wells list based on parameters.filter
      return;
    }

    if (action === 'start_data_entry' || action === 'create_production_entry') {
      const targetWell = findWellByIdentifier(wellId);
      if (targetWell) {
        setSelectedWell(targetWell);
        toast.success(`Opening production entry for ${targetWell.name}`);
      } else {
        setSelectedWell(null);
        if (wellId) {
          toast.error(`Well not found: ${wellId}`);
        } else {
          toast.success('Opening production entry');
        }
      }
      setShowDataEntry(true);
      return;
    }

    if (action === 'create_ticket') {
      setActiveTab('tickets');
      toast.success('Opening service tickets');
      return;
    }

    if (action === 'show_schedule') {
      setActiveTab('schedule');
      toast.success('Opening schedule');
      return;
    }

    if (action === 'show_alerts') {
      setShowAlerts(true);
      toast.success('Opening smart alerts');
      return;
    }

    if (action === 'sync_data') {
      toast.info('Syncing data...');
      // Trigger sync logic here
      // For now, we'll just show a toast as the sync is event-driven/background
      // You might want to expose a manual sync trigger from enterpriseSync
      return;
    }

    if (action === 'logout') {
      handleLogout();
      return;
    }

    if (cmd.intent && cmd.intent !== 'unknown') {
      toast.info(`Command "${action}" received`);
    }
  };

  // Handle well selection with automatic draft editing
  const handleSelectWell = (well: any) => {

    if (well.wellStatus === 'draft') {
      // Draft well - open form for editing
      setWellToEdit(well);
      setShowWellEditor(true);
      toast.info(`Resuming draft: ${well.name}`, {
        description: 'Continue editing your well'
      });
    } else {
      // Active well - show details
      setSelectedWell(well);
    }
  };

  // Handle well deletion request
  const handleDeleteWell = (well: any) => {
    setWellToDelete(well);
    setShowDeleteConfirmation(true);
    setDeleteConfirmText(''); // Reset confirmation text
  };

  // Confirm and execute well deletion
  const confirmDeleteWell = async () => {
    if (!wellToDelete) return;

    const isDraft = wellToDelete.wellStatus === 'draft';
    const requiresNameConfirmation = !isDraft; // Active wells require typing name

    // Validation for active wells
    if (requiresNameConfirmation && deleteConfirmText !== wellToDelete.name) {
      toast.error('Well name does not match');
      return;
    }

    setIsDeleting(true);

    try {
      const companyId = getCompanyId();
      const wellId = wellToDelete.id;


      // Delete Firestore document
      try {
        await deleteWellDocument(companyId, wellId);
      } catch (error: any) {
        if (error?.code === 'not-found') {
        } else {
          throw error;
        }
      }

      // Delete Storage folder recursively
      try {
        const storagePath = `companies/${companyId}/${wellId}`;
        await deleteStorageFolder(storagePath);
      } catch (error) {
        console.error('⚠️ Error deleting storage (may not exist):', error);
        // Continue - storage cleanup is non-critical
      }


      // Close confirmation and refresh
      setShowDeleteConfirmation(false);
      setWellToDelete(null);
      setDeleteConfirmText('');

      // Refresh wells list
      await refreshWells();

      // Clear selected well if it was the deleted one
      if (selectedWell?.id === wellId) {
        setSelectedWell(null);
      }

      toast.success(
        isDraft ? 'Draft deleted' : 'Well deleted successfully',
        {
          description: `${wellToDelete.name} has been permanently removed`
        }
      );

    } catch (error) {
      console.error('❌ Error deleting well:', error);
      toast.error('Failed to delete well', {
        description: error instanceof Error ? error.message : 'Unknown error occurred'
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const cancelDeleteWell = () => {
    setShowDeleteConfirmation(false);
    setWellToDelete(null);
    setDeleteConfirmText('');
  };

  const handleNavigate = (screen: string) => {
    if (screen === 'alerts') setShowAlerts(true);
    else if (screen === 'map') setActiveTab('satellite');
    else if (screen === 'navigation') {
      setNavigationRoute(null);
      setShowLaunchDialog(true);
    }
    else if (screen === 'compare') setShowComparison(true);
    else if (screen === 'quickEntry') {
      setSelectedWell(null);
      setShowDataEntry(true);
    }
    else if (screen === 'add') setShowDataEntry(true);
    else if (screen === 'billing') setShowBillingDashboard(true);
    else if (screen === 'company') setShowCompanyManagement(true);
    else if (screen === 'wellchecks') setShowWellChecks(true);
    else if (screen === 'equipment') setShowEquipment(true);
    else if (screen === 'gallery') setShowGallery(true);
    else if (screen === 'industryMap') setShowIndustryMap(true);
    else setActiveTab(screen);
  };

  // Show login screen if not authenticated (AFTER all hooks are called)
  if (!isAuthenticated) {
    return <LoginScreen onLoginSuccess={handleLoginSuccess} />;
  }

  // 👑 Show SuperAdmin Dashboard for superadmin users
  if (isSuperAdmin) {
    return <SuperAdminDashboard user={user} onLogout={handleLogout} />;
  }

  if (wellsLoading && wells.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center px-4">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-600 border-t-transparent mx-auto mb-4"></div>
          <p className="text-gray-900">Loading wells data...</p>
          <p className="text-sm text-gray-500 mt-2">This may take a few seconds</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen pb-20 md:pb-0 flex flex-col ${isDarkMode ? 'bg-slate-950' : 'bg-gray-50'}`}>
      {/* Header with Top Navigation */}
      {/* App Layout Wrapper */}
      <AppLayout
        activeTab={activeTab}
        companyName={companyName}
        companyLogo={companyLogo}
        onNavigate={handleNavigate}
        onOpenXAssist={() => setShowXAssist(true)}
        isVoiceListening={isVoiceListening}
        onToggleVoiceListening={toggleVoiceListening}
        isDarkMode={isDarkMode}
        onToggleTheme={toggleTheme}
        isClockedIn={isClockedIn}
        onToggleTimeClock={() => setShowTimeClock(true)}
        user={user ? {
          name: user.name || 'User',
          role: user.role || 'Viewer',
          email: user.email || '',
          isOnline: navigator.onLine
        } : undefined}
        onOpenProfile={() => setShowUserSettings(true)}
        onLogout={handleLogout}
        viewAsField={viewAsField}
        onReturnToCommandCenter={onReturnToCommandCenter}
      >
        {/* Overdue Wells Alert */}
        {overdueWells.length > 0 && activeTab !== 'superadmin' && (
          <div className="px-4 pt-3 md:pt-4">
            <Alert className={`${isDarkMode ? 'bg-orange-900/20 border-orange-800' : 'border-orange-200 bg-orange-50'}`}>
              <AlertTriangle className={`h-4 w-4 ${isDarkMode ? 'text-orange-500' : 'text-orange-600'}`} />
              <AlertDescription className={isDarkMode ? 'text-orange-300' : 'text-orange-800'}>
                {overdueWells.length} wells haven't been checked in over 7 days
              </AlertDescription>
            </Alert>
          </div>
        )}

        {/* Main Content - Full Width */}
        <main className="px-4 py-4 md:max-w-7xl md:mx-auto md:px-6 md:py-6 flex-1">
          {activeTab === 'dashboard' && (
            <Dashboard
              wells={wells}
              overdueWells={overdueWells}
              onSelectWell={(well) => {
                setSelectedWell(well);
                setActiveTab('wells');
              }}
              onAddData={() => setShowDataEntry(true)}
              onShowVoiceAssistant={() => setShowXAssist(true)}
              onNavigate={(screen) => {
                if (screen.startsWith('well-view-listings')) {
                  const params = new URLSearchParams(screen.split('?')[1]);
                  const filter = params.get('filter') || 'all';
                  const sort = params.get('sort');

                  setWellListFilter(filter);

                  if (sort === 'oil') {
                    setWellListSort({ key: 'oilProduction', direction: 'desc' });
                  } else if (sort === 'water') {
                    setWellListSort({ key: 'waterProduction', direction: 'desc' });
                  } else {
                    setWellListSort(null);
                  }

                  setActiveTab('well-view-listings');
                }
                else if (screen === 'alerts') setShowAlerts(true);
                else if (screen === 'map') setShowMap(true);
                else if (screen === 'compare') setShowComparison(true);
                else if (screen === 'add') setShowDataEntry(true);
                else setActiveTab(screen);
              }}
              user={user}
              isDarkMode={isDarkMode}
              onToggleTheme={toggleTheme}
            />
          )}

          {activeTab === 'well-view-listings' && (
            <WellViewListings
              wells={wells}
              initialFilter={wellListFilter}
              initialSort={wellListSort}
              onBack={() => setActiveTab('dashboard')}
              onSelectWell={(well) => {
                setSelectedWell(well);
                setActiveTab('wells');
              }}
            />
          )}

          {activeTab === 'satellite' && (
            <SatelliteCommand
              wells={wells}
              tickets={serviceTickets}
              tasks={tasks}
              employees={pumpers}
              onClose={() => setActiveTab('home')}
              onSelectWell={(well) => {
                setSelectedWell(well);
              }}
              onAddEntry={(well) => {
                setSelectedWell(well);
                setShowDataEntry(true);
              }}
              onCreateTicket={(well) => {
                setServiceTicketWellId(well.id);
                setActiveTab('tickets');
              }}
              onCreateTask={(well) => {
                setTaskWellId(well.id);
                setShowTaskManager(true);
              }}
              onOpenTanks={(well) => {
                setMonitorWell(well);
                setShowTankMonitor(true);
              }}
              onOpenCams={(well) => {
                setMonitorWell(well);
                setShowSurveillance(true);
              }}
              onNavigate={handleNavigateToWell}
            />
          )}

          {activeTab === 'wells' && (
            <>
              {selectedWell ? (
                <WellDetail
                  well={selectedWell}
                  wells={wells}
                  onDataEntry={() => setShowDataEntry(true)}
                  onRefresh={refreshWells}
                  onUpdateWell={updateWell}
                  onBack={() => setSelectedWell(null)}
                  onWellChange={(well) => setSelectedWell(well)}
                  onNavigateToWell={handleNavigateToWell}
                  user={user}
                  isDarkMode={isDarkMode}
                  onNavigateToServiceTickets={(prefillData) => {
                    if (prefillData) {
                      sessionStorage.setItem('serviceTicketPrefill', JSON.stringify(prefillData));
                    }
                    setActiveTab('tickets');
                  }}
                />
              ) : (
                <WellsListWithLocation
                  wells={filteredWells}
                  onSelectWell={handleSelectWell}
                  onDeleteWell={handleDeleteWell}
                  onAddEntry={(well) => {
                    setSelectedWell(well);
                    setShowDataEntry(true);
                  }}
                  onAddWell={() => {
                    setWellToEdit(null);
                    setShowWellEditor(true);
                  }}
                  onNavigateToWell={handleNavigateToWell}
                  isDarkMode={isDarkMode}
                />
              )}
            </>
          )}
          {activeTab === 'analytics' && (
            <div className="h-full">
              <AnalyticsMobile
                wells={wells}
                isDarkMode={isDarkMode}
                onSelectWell={(well) => {
                  setSelectedWell(well);
                  setActiveTab('wells');
                }}
              />
            </div>
          )}

          {activeTab === 'routes' && (
            <div className="h-full">
              <RoutesMobile
                wells={wells}
                onSelectWell={(well) => {
                  setSelectedWell(well);
                  setActiveTab('wells');
                }}
                currentUser={user ? { id: user.id, name: user.name } : undefined}
                onStartNavigation={openNavigation}
                isDarkMode={isDarkMode}
              />
            </div>
          )}

          {activeTab === 'tickets' && (
            <div className="h-full">
              <ServiceTickets
                wells={wells}
                currentUser={{ id: user.id, name: user.name, email: user.email }}
                initialWellId={serviceTicketWellId}
                onNavigate={(screen) => {
                  if (screen === 'alerts') setShowAlerts(true);
                  else if (screen === 'map') setShowMap(true);
                  else if (screen === 'compare') setShowComparison(true);
                  else if (screen === 'add') setShowDataEntry(true);
                  else setActiveTab(screen);
                }}
                onOpenXAssist={() => setShowXAssist(true)}
                isDarkMode={isDarkMode}
                onToggleTheme={toggleTheme}
                onClose={() => {
                  setServiceTicketWellId(null);
                  if (activeTab === 'tickets') setActiveTab('dashboard');
                }}
              />
            </div>
          )}

          {activeTab === 'drafts' && isFeatureEnabled('AUTO_SAVE_DATA_ENTRY') && (
            <div className="h-full">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center space-x-2">
                    <FileText className="h-5 w-5" />
                    <span>Draft Entries</span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <DraftEntriesDashboard
                    onResumeDraft={(draft) => {
                      const well = wells.find(w => w.id === draft.wellId);
                      if (well) {
                        setSelectedWell(well);
                        setActiveTab('wells');
                        setShowDataEntry(true);
                      } else {
                        toast.error('Well not found');
                      }
                    }}
                  />
                </CardContent>
              </Card>
            </div>
          )}

          {activeTab === 'more' && (
            <SettingsTab
              user={user}
              wells={wells}
              companyName={companyName}
              onWellsUpdate={(updatedWells) => {
                // Update wells - in production, this would sync to backend
                refreshWells();
              }}
              onShowMessaging={() => {
                setShowMessaging(true);
                setActiveTab('dashboard');
              }}
              onShowShiftHandoff={() => {
                setShowShiftHandoff(true);
                setActiveTab('dashboard');
              }}
              onShowTaskManager={() => {
                setShowTaskManager(true);
                setActiveTab('dashboard');
              }}
              onShowConflictResolver={() => {
                setShowConflictResolver(true);
                setActiveTab('dashboard');
              }}
              onShowRouteOptimizer={() => {
                setActiveTab('routes');
              }}
              onShowVoiceAssistant={() => {
                setShowXAssist(true);
                setActiveTab('dashboard');
              }}
              onShowTimeClock={() => {
                setShowTimeClock(true);
                setActiveTab('dashboard');
              }}
              onShowServiceTickets={() => setActiveTab('tickets')}
              onShowNotificationSettings={() => {
                setShowNotificationSettings(true);
                setActiveTab('dashboard');
              }}
              onNavigate={handleNavigate}
              onUserUpdate={(updatedUser) => {
                // Update user state when preferences change in SettingsTab
                setUser(updatedUser);
                // Also update localStorage for persistence
                localStorage.setItem('auth_session', JSON.stringify(updatedUser));
              }}
              isDarkMode={isDarkMode}
              onToggleTheme={toggleTheme}
            />
          )}

          {activeTab === 'superadmin' && (
            <SuperAdminDashboard user={user} onLogout={handleLogout} />
          )}
        </main>
      </AppLayout>



      {/* User/Settings Menu Drawer */}
      {showMobileMenu && (
        <div className="fixed inset-0 z-50">
          <div
            className="absolute inset-0 bg-black bg-opacity-50"
            onClick={() => setShowMobileMenu(false)}
          ></div>
          <div className="absolute right-0 top-0 bottom-0 w-80 max-w-[85vw] bg-white shadow-xl overflow-hidden">
            <div className="h-full flex flex-col overflow-hidden">
              {/* Header */}
              <div className="flex-shrink-0 p-4 border-b bg-gradient-to-r from-blue-600 to-blue-700">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="font-semibold text-white text-lg">Users</h2>
                  <button
                    onClick={() => setShowMobileMenu(false)}
                    aria-label="Close mobile menu"
                    className="p-2 rounded-lg hover:bg-white/20 active:bg-white/30 transition-colors"
                  >
                    <X className="h-6 w-6 text-white" />
                  </button>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="h-12 w-12 rounded-full bg-white/20 flex items-center justify-center">
                    <User className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <p className="font-medium text-white">{user.email}</p>
                    <Badge className="mt-1 bg-white/20 text-white border-white/30">
                      {user.role === 'superadmin' ? 'Super Admin' :
                        user.role === 'admin' ? 'Admin' :
                          user.role === 'manager' ? 'Manager' :
                            user.role === 'pumper' ? 'Pumper' :
                              user.role}
                    </Badge>
                  </div>
                </div>
              </div>

              {/* Menu Items */}
              <div className="flex-1 overflow-y-auto p-4">
                <div className="space-y-1">
                  {/* Drafts - Auto-Save Entries */}
                  {isFeatureEnabled('AUTO_SAVE_DATA_ENTRY') && (
                    <button
                      onClick={() => {
                        setShowDraftsDashboard(true);
                        setShowMobileMenu(false);
                      }}
                      className="w-full flex items-center space-x-3 px-4 py-3.5 rounded-lg text-gray-700 hover:bg-gray-100 active:bg-gray-200 transition-colors"
                    >
                      <FileText className="h-5 w-5" />
                      <span className="font-medium">Draft Entries</span>
                    </button>
                  )}

                  {/* Desktop-only menu items visible here on mobile */}
                  <button
                    onClick={() => {
                      setActiveTab('routes');
                      setShowMobileMenu(false);
                    }}
                    className="w-full flex items-center space-x-3 px-4 py-3.5 rounded-lg text-gray-700 hover:bg-gray-100 active:bg-gray-200 transition-colors"
                  >
                    <Route className="h-5 w-5" />
                    <span className="font-medium">Route Optimizer</span>
                  </button>

                  <button
                    onClick={() => {
                      setShowComparison(true);
                      setShowMobileMenu(false);
                    }}
                    className="w-full flex items-center space-x-3 px-4 py-3.5 rounded-lg text-gray-700 hover:bg-gray-100 active:bg-gray-200 transition-colors"
                  >
                    <Copy className="h-5 w-5" />
                    <span className="font-medium">Compare Wells</span>
                  </button>

                  {/* Manager Portal - Show when manager is in field view */}
                  {viewAsField && onReturnToCommandCenter && (
                    <button
                      onClick={() => {
                        setShowMobileMenu(false);
                        onReturnToCommandCenter();
                      }}
                      className="w-full flex items-center space-x-3 px-4 py-3.5 rounded-lg text-blue-700 bg-blue-50 hover:bg-blue-100 active:bg-blue-200 transition-colors"
                    >
                      <Settings className="h-5 w-5" />
                      <span className="font-medium">Manager Portal</span>
                    </button>
                  )}

                  {isSuperAdmin && (
                    <button
                      onClick={() => {
                        setActiveTab('superadmin');
                        setShowMobileMenu(false);
                      }}
                      className="w-full flex items-center space-x-3 px-4 py-3.5 rounded-lg text-purple-700 bg-purple-50 hover:bg-purple-100 active:bg-purple-200 transition-colors"
                    >
                      <Settings className="h-5 w-5" />
                      <span className="font-medium">SuperAdmin Panel</span>
                    </button>
                  )}

                  <button
                    onClick={() => {
                      setShowUserSettings(true);
                      setShowMobileMenu(false);
                    }}
                    className="w-full flex items-center space-x-3 px-4 py-3.5 rounded-lg text-gray-700 hover:bg-gray-100 active:bg-gray-200 transition-colors"
                  >
                    <Settings className="h-5 w-5" />
                    <span className="font-medium">Settings</span>
                  </button>

                  <button
                    onClick={() => {
                      setShowNotificationSettings(true);
                      setShowMobileMenu(false);
                    }}
                    className="w-full flex items-center space-x-3 px-4 py-3.5 rounded-lg text-gray-700 hover:bg-gray-100 active:bg-gray-200 transition-colors"
                  >
                    <Bell className="h-5 w-5" />
                    <span className="font-medium">Notification Settings</span>
                  </button>

                  {isSuperAdmin && (
                    <button
                      onClick={() => {
                        setShowDebugPanel(true);
                        setShowMobileMenu(false);
                      }}
                      className="w-full flex items-center space-x-3 px-4 py-3.5 rounded-lg text-gray-700 hover:bg-gray-100 active:bg-gray-200 transition-colors"
                    >
                      <Settings className="h-5 w-5" />
                      <span className="font-medium">Admin Debug Panel</span>
                    </button>
                  )}
                </div>
              </div>

              {/* Footer - Logout */}
              <div className="flex-shrink-0 p-4 border-t bg-gray-50">
                <Button
                  variant="outline"
                  className="w-full h-12 text-red-600 border-red-200 hover:bg-red-50"
                  onClick={() => {
                    setShowMobileMenu(false);
                    handleLogout();
                  }}
                >
                  <LogOut className="h-5 w-5 mr-2" />
                  Logout
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Notifications Panel */}
      {showNotifications && (
        <div className="fixed inset-0 z-50">
          <div
            className="absolute inset-0 bg-black bg-opacity-50"
            onClick={() => setShowNotifications(false)}
          ></div>
          <div className="absolute right-0 top-0 bottom-0 w-full md:w-96 bg-white shadow-xl overflow-hidden flex flex-col">
            <div className="flex-shrink-0 bg-white border-b px-4 py-3 flex items-center justify-between">
              <h2 className="font-semibold text-gray-900 text-lg">Notifications</h2>
              <button
                onClick={() => setShowNotifications(false)}
                aria-label="Close notifications"
                className="p-2 rounded-lg hover:bg-gray-100 active:bg-gray-200 transition-colors"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              <Notifications />
            </div>
          </div>
        </div>
      )}

      {/* Alert Center */}
      {showAlertCenter && user && (
        <AlertCenter
          onClose={() => setShowAlertCenter(false)}
        />
      )}

      {/* Data Entry Modal */}
      {showDataEntry && (
        <DataEntry
          well={selectedWell}
          wells={wells}
          isDarkMode={isDarkMode}
          onClose={() => setShowDataEntry(false)}
          onSave={async () => {
            setShowDataEntry(false);

            // Mark entry created for well departure tracking
            if (selectedWell?.id) {
              wellDepartureService.markEntryCreated(selectedWell.id);
            }

            // PHASE 5: Refresh wells metadata
            await refreshWells();

            // PHASE 5: Refresh production entries for selected well
            if (selectedWell?.id) {
              try {
                const companyId = getCompanyId();
                const entries = await fetchWellEntries(companyId, selectedWell.id, 10);

                // Update the selected well with fresh production data
                // This ensures the WellDetail component shows the latest entry
                setSelectedWell(prev =>
                  prev ? { ...prev, productionEntries: entries } : null
                );
              } catch (error) {
                console.error('Failed to refresh production entries:', error);
                // Don't block the success flow - data is already saved
              }
            }

            toast.success('Data saved successfully');
          }}
        />
      )}

      {/* Draft Entries Dashboard */}
      {showDraftsDashboard && user && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl font-bold">Draft Entries</h2>
              <Button variant="ghost" size="icon" onClick={() => setShowDraftsDashboard(false)}>
                <X className="h-5 w-5" />
              </Button>
            </div>
            <DraftEntriesDashboard
              onResumeDraft={(draft) => {
                // Find the well and open auto-save entry
                const well = wells.find(w => w.id === draft.wellId);
                if (well) {
                  setSelectedWell(well);
                  setShowDraftsDashboard(false);
                  setShowDataEntry(true);
                }
              }}
            />
          </div>
        </div>
      )}

      {/* Well Editor Modal */}
      {showWellEditor && (
        <ComprehensiveWellForm
          well={wellToEdit}
          onClose={() => setShowWellEditor(false)}
          onSave={() => {
            setShowWellEditor(false);
            setActiveTab('wells');      // Navigate to Wells tab
            refreshWells();
            // Toast already shown in form with success animation
          }}
          isDarkMode={isDarkMode}
        />
      )}

      {/* Well Comparison */}
      {showComparison && (
        <WellComparison
          wells={wells}
          onClose={() => setShowComparison(false)}
          isDarkMode={isDarkMode}
        />
      )}

      {/* Delete Well Confirmation Modal */}
      {showDeleteConfirmation && wellToDelete && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6 space-y-6">
            {/* Header */}
            <div className="space-y-2">
              <h2 className="text-2xl font-bold text-gray-900">
                {wellToDelete.wellStatus === 'draft' ? 'Delete Draft?' : 'Delete Well?'}
              </h2>
              <p className="text-gray-600">
                {wellToDelete.wellStatus === 'draft'
                  ? 'This draft will be permanently deleted.'
                  : 'This action cannot be undone. All data associated with this well will be permanently removed.'}
              </p>
            </div>

            {/* Well Info */}
            <div className="bg-gray-50 rounded-lg p-4 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Well Name</span>
                <span className="font-semibold text-gray-900">{wellToDelete.name}</span>
              </div>
              {wellToDelete.apiNumber && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">API Number</span>
                  <span className="font-mono text-sm text-gray-900">{wellToDelete.apiNumber}</span>
                </div>
              )}
              {wellToDelete.wellStatus && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Status</span>
                  <Badge variant={wellToDelete.wellStatus === 'draft' ? 'secondary' : 'default'}>
                    {wellToDelete.wellStatus}
                  </Badge>
                </div>
              )}
            </div>

            {/* Type-to-confirm for active wells */}
            {wellToDelete.wellStatus !== 'draft' && (
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">
                  Type <span className="font-mono bg-gray-100 px-2 py-0.5 rounded">{wellToDelete.name}</span> to confirm:
                </label>
                <input
                  type="text"
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  placeholder="Enter well name"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  autoFocus
                  disabled={isDeleting}
                />
              </div>
            )}

            {/* Warning */}
            {wellToDelete.wellStatus !== 'draft' && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start space-x-2">
                <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-red-800">
                  <p className="font-semibold">This will delete:</p>
                  <ul className="list-disc list-inside mt-1 space-y-0.5">
                    <li>Well document and metadata</li>
                    <li>All production entries</li>
                    <li>All photos and voice memos</li>
                    <li>All satellite data</li>
                  </ul>
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex items-center justify-end space-x-3 pt-4 border-t">
              <Button
                onClick={cancelDeleteWell}
                disabled={isDeleting}
                variant="outline"
              >
                Cancel
              </Button>
              <Button
                onClick={confirmDeleteWell}
                disabled={
                  isDeleting ||
                  (wellToDelete.wellStatus !== 'draft' && deleteConfirmText !== wellToDelete.name)
                }
                className={`${wellToDelete.wellStatus === 'draft'
                  ? 'bg-gray-600 hover:bg-gray-700'
                  : 'bg-red-600 hover:bg-red-700'
                  }`}
              >
                {isDeleting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="h-4 w-4 mr-2" />
                    {wellToDelete.wellStatus === 'draft' ? 'Delete Draft' : 'Delete Well'}
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Offline Indicator */}
      <OfflineIndicator />

      {/* Smart Alerts */}
      {showAlerts && (
        <SmartAlerts
          onClose={() => setShowAlerts(false)}
        />
      )}

      {/* Advanced Analytics */}
      {showAnalytics && selectedWell && (
        <AdvancedAnalytics
          well={selectedWell}
          productionData={selectedWell?.productionHistory || []}
          onClose={() => setShowAnalytics(false)}
        />
      )}

      {/* Enhanced Map */}


      {/* Satellite Command Center */}
      {showMap && (
        <SatelliteCommand
          wells={wells}
          employees={pumpers}
          tickets={serviceTickets}
          tasks={tasks}
          onClose={() => setShowMap(false)}
          onSelectWell={(well) => {
            setSelectedWell(well);
          }}
          onAddEntry={(well) => {
            setSelectedWell(well);
            setShowDataEntry(true);
            setShowMap(false);
          }}
          onCreateTicket={(well) => {
            setSelectedWell(well);
            setActiveTab('tickets');
            setShowMap(false);
            toast.info(`Create ticket for ${well.name}`);
          }}
          onCreateTask={(well) => {
            setTaskWellId(well.id);
            setShowTaskManager(true);
            setShowMap(false);
          }}
          onOpenTanks={(well) => {
            setMonitorWell(well);
            setShowTankMonitor(true);
            setShowMap(false);
          }}
          onOpenCams={(well) => {
            setMonitorWell(well);
            setShowSurveillance(true);
            setShowMap(false);
          }}
          onNavigate={handleNavigateToWell}
        />
      )}

      {/* Tank Monitor Modal */}
      {showTankMonitor && monitorWell && (
        <TankMonitor
          well={monitorWell}
          onClose={() => setShowTankMonitor(false)}
          onCreateTicket={(well) => {
            setSelectedWell(well);
            setActiveTab('tickets');
            setShowTankMonitor(false);
            toast.info(`Create ticket for ${well.name}`);
          }}
          isDarkMode={isDarkMode}
        />
      )}

      {/* Surveillance Hub Modal */}
      {showSurveillance && monitorWell && (
        <SurveillanceHub
          well={monitorWell}
          onClose={() => setShowSurveillance(false)}
          isDarkMode={isDarkMode}
        />
      )}

      {/* Industry Map (Manager/Owner Only - OCC Data & All Wells) */}
      {showIndustryMap && (
        <IndustryMap
          wells={wells}
          employees={pumpers}
          tickets={serviceTickets}
          tasks={tasks}
          onClose={() => setShowIndustryMap(false)}
          onSelectWell={(well) => {
            setSelectedWell(well);
          }}
          onAddEntry={(well) => {
            setSelectedWell(well);
            setShowDataEntry(true);
            setShowIndustryMap(false);
          }}
          onCreateTicket={(well) => {
            setSelectedWell(well);
            setActiveTab('tickets');
            setShowIndustryMap(false);
            toast.info(`Create ticket for ${well.name}`);
          }}
          onCreateTask={(well) => {
            setTaskWellId(well.id);
            setShowTaskManager(true);
            setShowIndustryMap(false);
          }}
          onOpenTanks={(well) => {
            setMonitorWell(well);
            setShowTankMonitor(true);
            setShowIndustryMap(false);
          }}
          onOpenCams={(well) => {
            setMonitorWell(well);
            setShowSurveillance(true);
            setShowIndustryMap(false);
          }}
        />
      )}



      {/* Time Clock - For Pumpers */}
      {showTimeClock && (
        <TimeClock
          userId={user.id}
          userName={user.name}
          userRole={user.role}
          companyId={user.companyId || user.companyId}
          onClose={() => setShowTimeClock(false)}
          onClockStatusChange={(clocked: boolean) => setIsClockedIn(clocked)}
        />
      )}





      {/* NEW COLLABORATION & OFFLINE FEATURES */}

      {/* Messaging Center */}
      {showMessaging && (
        <MessagingCenter
          currentUserId={user.id}
          currentUserName={user.name}
          onClose={() => setShowMessaging(false)}
        />
      )}

      {/* Task Manager */}
      {showTaskManager && (
        <TaskManager
          currentUserId={user.id}
          currentUserName={user.name}
          wells={wells}
          team={pumpers}
          initialWellId={taskWellId}
          onClose={() => {
            setShowTaskManager(false);
            setTaskWellId(null);
          }}
          isDarkMode={isDarkMode}
        />
      )}

      {/* Shift Handoff */}
      {showShiftHandoff && (
        <ShiftHandoff
          currentUserId={user.id}
          currentUserName={user.name}
          wells={wells}
          onClose={() => setShowShiftHandoff(false)}
        />
      )}

      {/* Conflict Resolver */}
      {showConflictResolver && (
        <ConflictResolver
          onResolved={() => {
            refreshWells();
            toast.success('Conflicts resolved');
          }}
          onClose={() => setShowConflictResolver(false)}
        />
      )}



      {/* X-Assist AI Voice & Chat Assistant */}
      {showXAssist && (
        <>
          {console.log('🎙️ Rendering XAssist modal', { showXAssist, wells: wells.length, user: user?.name })}
          <XAssist
            wells={wells}
            currentWell={selectedWell}
            tickets={serviceTickets}
            alerts={smartAlerts}
            userRole={user?.role}
            onCommand={(command) => {
              void handleAssistantCommand(command);
            }}
            onClose={() => setShowXAssist(false)}
          />
        </>
      )}



      {/* Security Setup */}
      {showSecuritySetup && (
        <SecuritySetup
          userId={user?.id || ''}
          userName={user?.name || ''}
          onClose={() => setShowSecuritySetup(false)}
        />
      )}

      {/* Lock Screen */}
      {isLocked && (
        <LockScreen
          userId={user?.id || ''}
          userName={user?.name || ''}
          onUnlock={() => {
            // Restore user from localStorage
            const savedUser = authService.getCurrentUser();
            if (savedUser) {
              setUser(savedUser);
              setIsAuthenticated(true);
              setIsSuperAdmin(savedUser.role === 'superadmin');
              setIsLocked(false);
              toast.success('Welcome back!');
            } else {
              toast.error('Could not restore session');
              setIsLocked(false);
            }
          }}
        />
      )}

      {/* Photo Annotator */}
      {showPhotoAnnotator && photoToAnnotate && (
        <PhotoAnnotator
          imageUrl={photoToAnnotate}
          onSave={(annotatedImage, annotations) => {
            toast.success('Photo annotation saved');
            setShowPhotoAnnotator(false);
            setPhotoToAnnotate(null);
            // Return to well editor if user was creating/editing a well
            setShowWellEditor(true);
          }}
          onClose={() => {
            setShowPhotoAnnotator(false);
            setPhotoToAnnotate(null);
            // Return to well editor if user was creating/editing a well
            setShowWellEditor(true);
          }}
        />
      )}

      {/* User Settings */}
      {showUserSettings && (
        <UserSettings
          user={user}
          onClose={() => setShowUserSettings(false)}
          onOpenMap={() => {
            setShowUserSettings(false);
            setShowMap(true);
          }}
          onSave={async (updatedUser) => {
            try {

              // Update user profile in Firestore
              await authService.updateUserProfile(user.id, {
                firstName: updatedUser.name?.split(' ')[0] || '',
                lastName: updatedUser.name?.split(' ').slice(1).join(' ') || '',
                phoneNumber: updatedUser.phone,
                phone: updatedUser.phone,
                company: updatedUser.company,
                location: updatedUser.location,
                profilePhoto: updatedUser.profilePhoto,
                preferences: updatedUser.preferences,
                security: updatedUser.security
              });


              // Always update company info (name and location)
              await authService.updateCompanyInfo(user.companyId, {
                name: updatedUser.company,
                address: {
                  street: '',
                  city: updatedUser.location || '',
                  state: '',
                  zip: ''
                }
              });


              // Update local user state first
              setUser(updatedUser);

              // Update company logo if changed
              if (updatedUser.companyLogo !== companyLogo) {
                setCompanyLogo(updatedUser.companyLogo || null);
              }

              // Update local company name state if changed
              if (updatedUser.company && updatedUser.company !== companyName) {
                setCompanyName(updatedUser.company);
              }

              toast.success('Profile updated successfully');
            } catch (error) {
              console.error('❌ Error saving profile:', error);
              toast.error('Failed to save profile changes');
            }
          }}
          isDarkMode={isDarkMode}
        />
      )}

      {/* Notification Settings */}
      {showNotificationSettings && user && (
        <NotificationSettings
          onClose={() => setShowNotificationSettings(false)}
          userId={user.id || user.uid}
          onSave={(settings) => {
            // Settings are now saved directly to Firestore in the component
          }}
        />
      )}

      {/* Billing Dashboard */}
      {showBillingDashboard && (
        <BillingDashboard
          onClose={() => setShowBillingDashboard(false)}
        />
      )}

      {/* Company Management */}
      {showCompanyManagement && (
        <CompanyManagement
          onClose={() => setShowCompanyManagement(false)}
          isSuperAdmin={isSuperAdmin || user?.role === 'manager'}
        />
      )}



      {/* Well Checks Dashboard */}
      {showWellChecks && (
        <WellCheckDashboard
          wells={wells}
          pumpers={pumpers}
        />
      )}

      {/* Equipment Management */}
      {showEquipment && (
        <EquipmentManagement
          wells={wells}
          currentUser={{ id: user?.id || '', name: user?.name || '' }}
          onClose={() => setShowEquipment(false)}
          isDarkMode={isDarkMode}
        />
      )}

      {/* Media Gallery */}
      {showGallery && (
        <MediaGallery
          companyId={user?.companyId || ''}
        />
      )}

      {showLaunchDialog && (
        <NavigatorLaunchDialog
          open={showLaunchDialog}
          onOpenChange={setShowLaunchDialog}
          destination={navigationRoute?.wells?.[0]?.coordinates ? {
            lat: navigationRoute.wells[0].coordinates.lat,
            lng: navigationRoute.wells[0].coordinates.lng,
            name: navigationRoute.wells[0].wellName
          } : null}
        />
      )}

      {/* Well Departure Reminder */}
      {departureReminder && (
        <WellDepartureReminder
          isVisible={departureReminder.isVisible}
          wellId={departureReminder.wellId}
          wellName={departureReminder.wellName}
          dwellDurationMs={departureReminder.dwellDurationMs}
          isDarkMode={isDarkMode}
          onLogEntry={() => {
            // Find the well and open data entry
            const well = wells.find((w: any) => w.id === departureReminder.wellId);
            if (well) {
              setSelectedWell(well);
              setShowDataEntry(true);
              // Mark that we're opening entry for this well
              wellDepartureService.markEntryCreated(departureReminder.wellId);
            }
            setDepartureReminder(null);
          }}
          onDismiss={() => {
            // Add cooldown for this well
            wellDepartureService.dismissReminder(departureReminder.wellId);
            setDepartureReminder(null);
          }}
        />
      )}

      {/* Footer - Copyright */}
      <footer className={`border-t py-2 mb-16 md:mb-0 ${isDarkMode ? 'bg-slate-900 border-slate-800 text-gray-400' : 'bg-white border-gray-200 text-gray-600'}`}>
        <div className="max-w-7xl mx-auto px-4 text-center">
          <div className={`flex flex-col md:flex-row items-center justify-center space-y-2 md:space-y-0 md:space-x-4 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            <span className="font-medium">OilWellX</span>
            <span className="hidden md:inline">|</span>
            <span>Copyright © 2026</span>
            <span className="hidden md:inline">|</span>
            <span>
              Developed by{' '}
              <a
                href="https://TGE.LA"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-700 font-medium underline"
              >
                TGE
              </a>
            </span>
            <span className="hidden md:inline">|</span>
            <a
              href="/privacy"
              className="text-gray-600 hover:text-blue-600 transition-colors"
            >
              Privacy Policy
            </a>
          </div>
          <p className="text-xs text-gray-500 mt-2">
            {companyName} Oil Well Management Platform
          </p>
        </div>
      </footer>

      {/* Admin Debug Panel - Click gear icon in bottom-right to open */}
      <AdminDebugPanel
        showPanel={showDebugPanel}
        onClose={() => setShowDebugPanel(false)}
      />

      {/* Exit Confirmation Dialog - shown when user presses back twice */}
      <ExitConfirmDialog
        open={showExitConfirmDialog}
        onConfirm={handleConfirmExit}
        onCancel={handleCancelExit}
      />

    </div>
  );
}
